/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {Dialog} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import {translate} from '@ion/core/src/locale/locale';
import {AccountLink} from '@ion/core/src/types/entities';
import Avatar from '@ion/dls/src/components/avatar/Avatar';
import {IconButton} from '@ion/dls/src/components/buttons/IconButton';
import {GlobalGroupChooserContent} from '@ion/dls/src/components/groupchooser/GlobalGroupChooserContent';
import {dlsColor} from '@ion/dls/src/theme/theme';

export interface GlobalGroupChooserDialogProps {
  open: boolean;
  onlyAccountId?: boolean;
  accountHierarchy: AccountLink[];
  onClose: MouseEventHandler<HTMLButtonElement>;
  selectedGroup: AccountLink;
  onSelectGroup: (value: AccountLink) => void;
  userName?: string;
  allCrumbsClickable?: boolean;
}

const useStyles = makeStyles(theme => ({
  dialogWrapper : {
    padding : '15px 26px 26px'
  },
  dialogHeader : {
    display        : 'flex',
    justifyContent : 'space-between',
    alignItems     : 'center',
    borderBottom   : `solid 1px ${dlsColor('grey5')}`,
    padding        : '0px 26px 11px',
    margin         : '0px -26px'
  },
  groupChooser : {
    marginTop : '0px'
  }
}));

/**
 * @GlobalGroupChooserDialog
 *
 * Dialog implementation of the global group chooser that renders
 * the GlobalGroupChooserContent inside the dialog.
 *
 * @param open if dialog is open
 * @param accountHierarchy hierarchy that chooser uses to build the options
 * @param onClose fired when dialog closes
 * @param selectedGroup currently selected group/account
 * @param onSelectGroup fired when user chooses a group option (but does not submit)
 * @param userName username that is displayed in the title of the dialog
 *
 * ```typescript
 * import {GlobalGroupChooserContent} from '@ion/dls/src/components/groupchooser/GlobalGroupChooserContent';
 *
 *   <GlobalGroupChooserDialog open={openChooser}
 *     accountHierarchy={accountHierarchy}
 *     onClose={onClose}
 *     selectedGroup={globalGroup}
 *     onSelectGroup={onSelectGroup}
 *     userName={username} />
 * ```
 */
export const GlobalGroupChooserDialog = ({
  open,
  accountHierarchy,
  onClose,
  selectedGroup,
  onSelectGroup,
  userName = '',
  onlyAccountId = false,
  allCrumbsClickable = false
}: GlobalGroupChooserDialogProps) => {
  const classes = useStyles();

  return (
    <Dialog fullScreen open={open}>
      <div className={classes.dialogWrapper}>
        <div className={classes.dialogHeader}>
          <IconButton size='small'
            onClick={onClose}
            icon={<ChevronLeft />} />
          <Typography variant='h2'>
            {translate('common:editors.shared.changeGroup')}
          </Typography>
          <div>
            <Avatar name={userName} />
          </div>
        </div>
        <div className={classes.groupChooser}>
          <GlobalGroupChooserContent groups={accountHierarchy}
            currentGroup={selectedGroup}
            onlyAccountId={onlyAccountId}
            onGroupApply={onSelectGroup}
            allCrumbsClickable={allCrumbsClickable}/>
        </div>
      </div>
    </Dialog>
  );
};

export default GlobalGroupChooserDialog;
